import { FC, ReactElement, useEffect } from 'react'
import { useSelector } from 'react-redux'

import {
    transformLegacyLeaveOfAbsenceToLeaveOfAbsence,
} from '../../../@paco/entities/LeaveOfAbsence/LeaveOfAbsenceTransformers'
import { PayrollPeriod } from '../../../@paco/entities/PayrollPeriod/PayrollPeriod'
import {
    approveLeaveOfAbsence,
    declineLeaveOfAbsence,
    deleteLeaveOfAbsence,
    patchLeaveOfAbsence,
} from '../../../@paco/redux/leaveOfAbsences/leaveOfAbsencesActions'
import { useTypedDispatch, useTypedSelector } from '../../../@paco/redux/store'
import { PzLeaveType } from '../../../@paco/types/leaveType'

import { CompletePeriodIncompleteLeaveOfAbsencesContainer } from '../../../containers'
import {
    LeaveOfAbsence,
    LeaveOfAbsenceEditType,
    LeaveOfAbsenceFormData,
    LeaveType,
    WeekWithHours,
} from '../../../models'
import {
    fetchIncompleteLeaveOfAbsences,
} from '../../../redux/@toolkit/leaveOfAbsences/incompleteLeaveOfAbsences/incompleteLeaveOfAbsencesActions'
import { Reducers } from '../../../redux/reducers'
import {
    transformPayrollPeriodToLegacyPayrollPeriod,
} from '../../../services/PayrollPeriodService/transformPayrollPeriodLegacy'

interface ConnectedCompletePeriodIncompleteLeaveOfAbsencesProps {
    activePeriod?: PayrollPeriod;
    className?: string;
}

const ConnectedCompletePeriodIncompleteLeaveOfAbsences: FC<ConnectedCompletePeriodIncompleteLeaveOfAbsencesProps> = ({ activePeriod, className }): ReactElement => {
    const dispatch = useTypedDispatch()

    const isLoadingIncompleteLeaveOfAbsences = useSelector<Reducers, boolean>(state => state.incompleteLeaveOfAbsencesReducer.isLoading)
    const isLoadingLeaveOfAbsence = useSelector<Reducers, boolean>(state => state.leaveOfAbsenceReducer.isLoading)
    const isSuccessful = useSelector<Reducers, boolean>(state => state.leaveOfAbsenceReducer.isSuccessful)
    const leaveOfAbsences = useSelector<Reducers, LeaveOfAbsence[]>(state => state.incompleteLeaveOfAbsencesReducer.incompleteLeaveOfAbsences)
    const { role: currentUserRole, permissions } = useTypedSelector(state => state.authenticatedUserReducer)
    const { payrollPeriods } = useTypedSelector(state => state.pacoPayrollPeriodsReducer)

    const handleSubmitLeaveOfAbsence = async (
        type: LeaveOfAbsenceEditType,
        data: LeaveOfAbsenceFormData,
        weeksWithHours: WeekWithHours[],
        leaveOfAbsence: LeaveOfAbsence,
        leaveType?: LeaveType | PzLeaveType,
        declineComment?: string,
    ): Promise<void> => {
        if (activePeriod) {
            switch (type) {
                case LeaveOfAbsenceEditType.accept:
                    await dispatch(approveLeaveOfAbsence(
                        transformLegacyLeaveOfAbsenceToLeaveOfAbsence(leaveOfAbsence),
                        data,
                        leaveOfAbsence.leaveOfAbsenceHours,
                        weeksWithHours,
                        leaveType,
                    ))
                    break
                case LeaveOfAbsenceEditType.deny:
                    await dispatch(declineLeaveOfAbsence(
                        transformLegacyLeaveOfAbsenceToLeaveOfAbsence(leaveOfAbsence),
                        declineComment || '',
                    ))
                    break
                case LeaveOfAbsenceEditType.delete:
                    await dispatch(deleteLeaveOfAbsence(
                        transformLegacyLeaveOfAbsenceToLeaveOfAbsence(leaveOfAbsence),
                    ))
                    break
                default:
                    await dispatch(patchLeaveOfAbsence(
                        transformLegacyLeaveOfAbsenceToLeaveOfAbsence(leaveOfAbsence),
                        data,
                        leaveOfAbsence.leaveOfAbsenceHours || [],
                        weeksWithHours,
                        leaveType,
                    ))
            }
        }
    }

    useEffect((): void => {
        if (activePeriod && isSuccessful) {
            const legacyPayrollPeriod = transformPayrollPeriodToLegacyPayrollPeriod(activePeriod)

            dispatch(fetchIncompleteLeaveOfAbsences(legacyPayrollPeriod))
        }
    }, [isSuccessful])

    return (
        <CompletePeriodIncompleteLeaveOfAbsencesContainer
            isLoading={isLoadingIncompleteLeaveOfAbsences || isLoadingLeaveOfAbsence}
            leaveOfAbsences={leaveOfAbsences}
            permissions={permissions}
            currentUserRole={currentUserRole}
            pacoPayrollPeriods={payrollPeriods}
            onSubmitLeaveOfAbsence={handleSubmitLeaveOfAbsence}
            className={className}
        />
    )
}

export default ConnectedCompletePeriodIncompleteLeaveOfAbsences
